import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db, storage } from '../Firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { 
  Plus, 
  Trash2, 
  X, 
  Upload, 
  ImageIcon, 
  ChevronRight
} from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { fileToBase64 } from '../utils/fileHelper';

const GalleryManager = () => {
  const [photos, setPhotos] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Event Photos',
    image: null
  });

  const categories = ['Event Photos', 'Office Images', 'Social Activity'];

  useEffect(() => {
    const q = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setPhotos(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 800 * 1024) {
        Swal.fire('Error', 'Image size should not exceed 800KB. Please upload an image below 800KB.', 'error');
        return;
      }
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) return;
    
    setLoading(true);
    try {
      const imageUrl = await fileToBase64(formData.image);

      await addDoc(collection(db, 'gallery'), {
        title: formData.title,
        category: formData.category,
        imageUrl,
        createdAt: new Date()
      });

      Swal.fire('Success', 'Photo added to gallery', 'success');
      setFormData({ title: '', category: 'Event Photos', image: null });
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to upload photo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photo) => {
    const result = await Swal.fire({
      title: 'Delete Photo?',
      text: "This image will be permanently removed from the gallery.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'gallery', photo.id));
        if (photo.imageUrl.includes('firebasestorage')) {
          await deleteObject(ref(storage, photo.imageUrl));
        }
        Swal.fire('Deleted!', 'Photo has been removed.', 'success');
      } catch (error) {
        Swal.fire('Error', 'Failed to delete photo', 'error');
      }
    }
  };

  const filteredPhotos = activeTab === 'All' ? photos : photos.filter(p => p.category === activeTab);

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Gallery Manager</h1>
          <p className="text-gray-500 font-medium">Curate and manage your bank's visual story.</p>
        </div>
        
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 transition-all active:scale-95 font-bold"
        >
          <Plus className="w-5 h-5" />
          Add New Photo
        </button>
      </div>

      {/* Tabs & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-4 rounded-[2rem] shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-2">
          {['All', ...categories].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab 
                ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        
        <div className="flex items-center gap-6 px-4">
          <div className="text-center">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Media</p>
            <p className="text-lg font-bold text-primary-dark">{photos.length}</p>
          </div>
          <div className="w-px h-8 bg-gray-100" />
          <div className="text-center">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Categories</p>
            <p className="text-lg font-bold text-secondary">{categories.length}</p>
          </div>
        </div>
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        <AnimatePresence>
          {filteredPhotos.map((photo, index) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ delay: index * 0.05 }}
              className="relative aspect-square group rounded-[2rem] overflow-hidden shadow-sm hover:shadow-xl transition-all"
            >
              <img src={photo.imageUrl} alt={photo.title || 'Gallery'} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-primary-dark/65 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4 text-center">
                <span className="bg-white/95 backdrop-blur-sm text-primary text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-2">
                  {photo.category}
                </span>
                <h4 className="text-white text-xs font-bold mb-3 px-2 line-clamp-2">
                  {photo.title || photo.category || 'Untitled'}
                </h4>
                <button
                  onClick={() => handleDelete(photo)}
                  className="p-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 shadow-xl transition-all hover:scale-110 active:scale-95"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[3rem] shadow-2xl w-full max-w-lg overflow-hidden max-h-[95vh] flex flex-col"
          >
            <div className="p-8 border-b border-gray-100 flex items-center justify-between shrink-0">
              <h2 className="text-2xl font-bold text-primary-dark">Add to Gallery</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Image Title</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter dynamic image title..."
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm font-semibold text-gray-800"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Select Category</label>
                <div className="grid grid-cols-1 gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setFormData({ ...formData, category: cat })}
                      className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                        formData.category === cat 
                        ? 'bg-primary/5 border-primary text-primary font-bold' 
                        : 'bg-gray-50 border-gray-100 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-sm">{cat}</span>
                      {formData.category === cat && <ChevronRight className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Upload Photo (Max 800KB)</label>
                <div className="relative h-56 group">
                  <input
                    type="file"
                    accept="image/*"
                    required
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="h-full border-2 border-dashed border-gray-200 rounded-[2.5rem] flex flex-col items-center justify-center gap-3 bg-gray-50 group-hover:bg-gray-100 group-hover:border-primary/30 transition-all overflow-hidden">
                    {formData.image ? (
                      <div className="relative w-full h-full">
                        <img 
                          src={URL.createObjectURL(formData.image)} 
                          className="w-full h-full object-cover" 
                          alt="Preview"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Upload className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-3">
                          <ImageIcon className="w-8 h-8 text-primary" />
                        </div>
                        <p className="text-gray-500 font-bold text-sm">Select Gallery Photo</p>
                        <p className="text-gray-400 text-xs mt-1">High resolution recommended</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-8 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.image}
                  className="flex-[2] px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-dark shadow-lg shadow-primary/20 transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? 'Uploading...' : 'Add to Gallery'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default GalleryManager;
